import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { useAuth } from '../contexts/AuthContext';
import { reportingService, SubscriptionReport, UserActivityReport, TenantUsageReport } from '../services/reportingService';
import { tenantService } from '../services/tenantService';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Reporting: React.FC = () => {
  const { tokens, login } = useAuth();
  
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  
  const [activeTab, setActiveTab] = useState<number>(0);
  const [tenants, setTenants] = useState<any[]>([]);
  
  // Report data
  const [subscriptionReport, setSubscriptionReport] = useState<SubscriptionReport | null>(null);
  const [userActivityReport, setUserActivityReport] = useState<UserActivityReport | null>(null);
  const [tenantUsageReport, setTenantUsageReport] = useState<TenantUsageReport | null>(null);
  
  // Filters
  const [filters, setFilters] = useState({
    dateRange: {
      startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
      endDate: new Date(),
    },
    tenantId: '',
  });

  // Initialize services and load data
  useEffect(() => {
    if (tokens && login) {
      reportingService.initialize(tokens, login);
      tenantService.initialize(tokens, login);
      loadTenants();
      loadAllReports();
    }
  }, [tokens, login]);

  const loadTenants = async () => {
    try {
      const response = await tenantService.getTenants({ page: 1, pageSize: 100 });
      if (response.succeeded) {
        const tenantsData = response.data.map((tenant: any) => ({
          id: tenant.id,
          name: tenant.name
        }));
        setTenants(tenantsData);
      }
    } catch (err) {
      console.error('Error loading tenants:', err);
    }
  };

  const loadAllReports = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const reportFilters = {
        dateRange: {
          startDate: filters.dateRange.startDate.toISOString(),
          endDate: filters.dateRange.endDate.toISOString(),
        },
        tenantId: filters.tenantId || undefined,
      };

      // Load all reports in parallel
      const [subscriptionResponse, userActivityResponse, tenantUsageResponse] = await Promise.all([
        reportingService.getSubscriptionReport(reportFilters),
        reportingService.getUserActivityReport(reportFilters),
        reportingService.getTenantUsageReport(reportFilters),
      ]);

      if (subscriptionResponse.succeeded) {
        setSubscriptionReport(subscriptionResponse.data);
      }
      if (userActivityResponse.succeeded) {
        setUserActivityReport(userActivityResponse.data);
      }
      if (tenantUsageResponse.succeeded) {
        setTenantUsageReport(tenantUsageResponse.data);
      }
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Failed to load reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRefresh = () => {
    loadAllReports();
  };

  const handleExport = async (reportType: string, format: 'csv' | 'pdf' | 'excel') => {
    try {
      const reportFilters = {
        dateRange: {
          startDate: filters.dateRange.startDate.toISOString(),
          endDate: filters.dateRange.endDate.toISOString(),
        },
        tenantId: filters.tenantId || undefined,
      };

      const blob = await reportingService.exportReport(reportType, format, reportFilters);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportType}-report-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSnackbar(`Report exported successfully as ${format.toUpperCase()}`, 'success');
    } catch (err) {
      console.error('Error exporting report:', err);
      showSnackbar('Failed to export report', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Chart configurations
  const subscriptionStatusChartData = subscriptionReport ? {
    labels: subscriptionReport.subscriptionsByStatus.map(item => item.status),
    datasets: [{
      data: subscriptionReport.subscriptionsByStatus.map(item => item.count),
      backgroundColor: [
        '#4caf50', // Active
        '#f44336', // Inactive
        '#ff9800', // Expired
        '#9c27b0', // Pending
      ],
    }],
  } : null;

  const userActivityChartData = userActivityReport ? {
    labels: userActivityReport.userActivityByMonth.map(item => item.month),
    datasets: [
      {
        label: 'Active Users',
        data: userActivityReport.userActivityByMonth.map(item => item.activeUsers),
        borderColor: '#2196f3',
        backgroundColor: 'rgba(33, 150, 243, 0.1)',
        tension: 0.1,
      },
      {
        label: 'New Users',
        data: userActivityReport.userActivityByMonth.map(item => item.newUsers),
        borderColor: '#4caf50',
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        tension: 0.1,
      },
    ],
  } : null;

  const tenantUsageChartData = tenantUsageReport ? {
    labels: tenantUsageReport.tenantUsageStats.map(item => item.tenantName),
    datasets: [{
      label: 'User Count',
      data: tenantUsageReport.tenantUsageStats.map(item => item.userCount),
      backgroundColor: '#2196f3',
    }],
  } : null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Report Data',
      },
    },
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Reports & Analytics
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh Reports">
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={filters.dateRange.startDate}
                onChange={(newValue) => handleFilterChange('dateRange', { ...filters.dateRange, startDate: newValue || new Date() })}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={filters.dateRange.endDate}
                onChange={(newValue) => handleFilterChange('dateRange', { ...filters.dateRange, endDate: newValue || new Date() })}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tenant</InputLabel>
              <Select
                value={filters.tenantId}
                onChange={(e) => handleFilterChange('tenantId', e.target.value)}
                label="Tenant"
              >
                <MenuItem value="">All Tenants</MenuItem>
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              onClick={loadAllReports}
              startIcon={<DateRangeIcon />}
              fullWidth
            >
              Apply Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Subscriptions" />
          <Tab label="User Activity" />
          <Tab label="Tenant Usage" />
        </Tabs>
      </Box>

      {/* Subscription Reports */}
      <TabPanel value={activeTab} index={0}>
        {subscriptionReport && (
          <Grid container spacing={3}>
            {/* Summary Cards */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Subscriptions
                  </Typography>
                  <Typography variant="h4">
                    {subscriptionReport.totalSubscriptions}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Active Subscriptions
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {subscriptionReport.activeSubscriptions}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Inactive Subscriptions
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {subscriptionReport.inactiveSubscriptions}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Expired Subscriptions
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {subscriptionReport.expiredSubscriptions}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Charts */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Subscriptions by Status
                  </Typography>
                  {subscriptionStatusChartData && (
                    <Doughnut data={subscriptionStatusChartData} options={chartOptions} />
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Subscriptions by Tenant
                  </Typography>
                  {subscriptionReport.subscriptionsByTenant.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>{item.tenantName}</Typography>
                      <Chip label={item.count} color="primary" size="small" />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            {/* Export Options */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Export Options
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleExport('subscriptions', 'csv')}
                    >
                      Export CSV
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleExport('subscriptions', 'pdf')}
                    >
                      Export PDF
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleExport('subscriptions', 'excel')}
                    >
                      Export Excel
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* User Activity Reports */}
      <TabPanel value={activeTab} index={1}>
        {userActivityReport && (
          <Grid container spacing={3}>
            {/* Summary Cards */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Users
                  </Typography>
                  <Typography variant="h4">
                    {userActivityReport.totalUsers}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Active Users
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {userActivityReport.activeUsers}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Inactive Users
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {userActivityReport.inactiveUsers}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    New Users This Month
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {userActivityReport.newUsersThisMonth}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Charts */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    User Activity Over Time
                  </Typography>
                  {userActivityChartData && (
                    <Line data={userActivityChartData} options={chartOptions} />
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Users by Role
                  </Typography>
                  {userActivityReport.usersByRole.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>{item.roleName}</Typography>
                      <Chip label={item.count} color="secondary" size="small" />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            {/* Export Options */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Export Options
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleExport('user-activity', 'csv')}
                    >
                      Export CSV
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleExport('user-activity', 'pdf')}
                    >
                      Export PDF
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleExport('user-activity', 'excel')}
                    >
                      Export Excel
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Tenant Usage Reports */}
      <TabPanel value={activeTab} index={2}>
        {tenantUsageReport && (
          <Grid container spacing={3}>
            {/* Summary Cards */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Tenants
                  </Typography>
                  <Typography variant="h4">
                    {tenantUsageReport.totalTenants}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Active Tenants
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {tenantUsageReport.activeTenants}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Inactive Tenants
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {tenantUsageReport.inactiveTenants}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Usage Stats
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {tenantUsageReport.tenantUsageStats.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Charts */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Tenant Usage Statistics
                  </Typography>
                  {tenantUsageChartData && (
                    <Bar data={tenantUsageChartData} options={chartOptions} />
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Tenant Status Distribution
                  </Typography>
                  {tenantUsageReport.tenantsByStatus.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>{item.status}</Typography>
                      <Chip label={item.count} color="primary" size="small" />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            {/* Export Options */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Export Options
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleExport('tenant-usage', 'csv')}
                    >
                      Export CSV
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleExport('tenant-usage', 'pdf')}
                    >
                      Export PDF
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleExport('tenant-usage', 'excel')}
                    >
                      Export Excel
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reporting;
